import { useIsMobileHook } from '@teliaee/telia-common-components';
import { TranslationsContext } from '@teliaee/telia-translate';
import { inject, observer } from 'mobx-react';
import React, { useEffect } from 'react';
import {
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  GridColumn,
  Heading,
  List,
  ListItem,
  Notice,
  Search,
  Separator,
  Spinner,
  TabCordion,
  TabsItem,
  Text,
  Toggle
} from 'telia-front-react';
import LanguageStore from '../stores/LanguageStore';
import PriceListStore from '../stores/PriceListStore';
import RouteStore from '../stores/RouteStore';
import SsoStore from '../stores/SsoStore';
import { FetchStatus } from '../util/FetchStatus';
import { langFromUrl, pathnameWithoutLang } from '../util/NavigationUtil';
import { getPriceListTabItemId } from '../util/PriceListUtil';
import { PriceListCategory } from './PriceListInterfaces';
import PriceListTabContent from './PriceListTabContent';

export interface PriceListComponentProps {
  priceListId: number;
  priceListsBaseUrl?: string;
  priceListStore?: PriceListStore;
  ssoStore?: SsoStore;
  languageStore?: LanguageStore;
  routeStore?: RouteStore;
}

const PriceListComponent = (props: PriceListComponentProps) => {
  const { isMobile } = useIsMobileHook();
  const translate = React.useContext(TranslationsContext).translate;

  const {
    priceListStore: {
      priceListToDisplay,
      priceListFetchStatus,
      debouncedPriceListSearch,
      handleCategoryChange,
      currentTab,
      searchTerm,
      clearSearch,
      pushPriceListGaData
    },
    ssoStore
  } = props;
  const isPrivateCustomer = ssoStore.customerType === 'private';
  const [showPricesWithVat, setShowPricesWithVat] = React.useState(true);

  useEffect(() => {
    props.priceListStore.fetchPriceList(props.priceListId);
    clearSearch();
  }, [props.languageStore.currentLanguage]);

  useEffect(() => {
    debouncedPriceListSearch(searchTerm, isPrivateCustomer, isPrivateCustomer ? false : !showPricesWithVat);
  }, [isPrivateCustomer, showPricesWithVat]);

  const getBackButtonUrl = () => {
    const {
      priceListsBaseUrl,
      routeStore: { langFromCurrentPath }
    } = props;
    const shouldChangeLang =
      (!!langFromCurrentPath && !priceListsBaseUrl.includes(langFromCurrentPath)) || (!langFromCurrentPath && !!langFromUrl(priceListsBaseUrl));
    return shouldChangeLang ? `${langFromCurrentPath ? `/${langFromCurrentPath}` : ''}${pathnameWithoutLang(priceListsBaseUrl)}` : priceListsBaseUrl;
  };

  const renderInfoSection = () => (
    <>
      <Grid noWrap={['xs']} align={['middle-xs']}>
        {props.priceListsBaseUrl && (
          <GridColumn width={['min-xs']}>
            <Button
              text={translate('pricelist.button.back')}
              url={getBackButtonUrl()}
              variant="secondary"
              icon="#arrow-left"
              onClick={() => window.location.assign(getBackButtonUrl())}
            />
          </GridColumn>
        )}
        <GridColumn width={['max-xs']}>
          <Heading element="h2">{priceListToDisplay.title}</Heading>
        </GridColumn>
      </Grid>
      <Separator color="invisible" spacing="small" />
      <Grid noWrap={['md']}>
        <GridColumn width={['max']}>
          <p className="text-bold h6">{translate('pricelist.legal.description')}</p>
          <Separator color="invisible" spacing="xxsmall" />
          <p>{translate('pricelist.activeFrom.date', { activeFrom: priceListToDisplay.activeFrom })}</p>
        </GridColumn>
        <GridColumn width={['min']}>
          <Button text={translate('pricelist.savePdf')} url={priceListToDisplay.pdfLink} target="_blank" variant="secondary" iconLeft="#download" />
        </GridColumn>
      </Grid>
      <Separator color="invisible" spacing="small" />
      <Grid align={['end-xs', 'start-md', 'middle-md']}>
        <GridColumn width={['max-xs', 'md-4']}>
          <Search
            id="find-keyword"
            label={translate('pricelist.search.label')}
            onChange={(value) => debouncedPriceListSearch(value, isPrivateCustomer, isPrivateCustomer ? false : !showPricesWithVat)}
            onClear={() => clearSearch()}
            onSubmit={(event) => event.preventDefault()}
          />
        </GridColumn>
        {!isPrivateCustomer && (
          <GridColumn width={['xs', 'md-4']}>
            <Toggle
              label={translate('pricelist.vatToggle.label')}
              id="vat-toggle"
              defaultChecked={showPricesWithVat}
              onChange={(checked) => {
                setShowPricesWithVat(!showPricesWithVat);
                pushPriceListGaData('VAT Toggle', `Toggle - ${checked ? 'On' : 'Off'}`);
              }}
            />
          </GridColumn>
        )}
      </Grid>
    </>
  );

  const renderTabItem = (category: PriceListCategory, categoryIndex: number) => {
    if (!!searchTerm && !category.containsSearchTerm) {
      return null;
    }

    return (
      <TabsItem
        id={getPriceListTabItemId(categoryIndex)}
        key={getPriceListTabItemId(categoryIndex)}
        label={`${categoryIndex}. ${category.categoryTitle}`}
        tagContent={category.searchTermHits ? category.searchTermHits : undefined}
      >
        <PriceListTabContent
          category={category}
          categoryIndex={categoryIndex}
          isPrivateCustomer={isPrivateCustomer}
          showPricesWithVat={showPricesWithVat}
          isFilteredResults={!!searchTerm}
        />
      </TabsItem>
    );
  };

  const renderNotFoundResults = () => (
    <>
      <Text className="text-center">
        {translate('search.wasFound')}
        <strong>
          {' 0 '}
          {translate('search.results')}{' '}
        </strong>
        <>{translate('search.forSearch')} </>"<strong>{searchTerm}</strong>".
      </Text>
      <Separator spacing="xsmall" color="invisible" />
      <Grid align={['center-xs']}>
        <GridColumn width={['xs-12', 'md-10', 'lg-9', 'xl-7']}>
          <Card type="light" padding={isMobile ? 'small' : 'large'} background="neutral-bg-level-1">
            <CardContent>
              <p className="h5 text-bold text-left">{translate('search.searchSuggestions.title')}</p>
              <Separator spacing="xsmall" color="invisible" />
              <List className="text-left" type="icon" icon="#check">
                {[...Array(2).keys()].map((suggestionNumber) => (
                  <ListItem key={`no-result-suggestion-${suggestionNumber}`}>{translate(`search.searchSuggestions.${suggestionNumber}`)}</ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </GridColumn>
      </Grid>
    </>
  );

  const renderPriceListTabs = () => {
    if (!!searchTerm && !priceListToDisplay.categories.some((category) => category.containsSearchTerm)) {
      return renderNotFoundResults();
    }

    return (
      <TabCordion
        currentTab={isMobile ? undefined : [currentTab]}
        defaultCurrentTab={getPriceListTabItemId(1)}
        border="top"
        aria-labelledby={priceListToDisplay.title}
        onTabChange={(_prevTab, nextTab) => handleCategoryChange(nextTab[0])}
      >
        {priceListToDisplay.categories.map((category, index) => renderTabItem(category, index + 1))}
      </TabCordion>
    );
  };

  if (priceListFetchStatus === FetchStatus.ERROR) {
    return (
      <Container>
        <Notice type="error">{translate('general.error.message')}</Notice>
      </Container>
    );
  }

  if (!priceListToDisplay || priceListFetchStatus === FetchStatus.FETCHING) {
    return <Spinner />;
  }

  return (
    <Container>
      {renderInfoSection()}
      <Separator color="invisible" spacing="small" />
      {renderPriceListTabs()}
    </Container>
  );
};

export default inject('priceListStore', 'ssoStore', 'languageStore', 'routeStore')(observer(PriceListComponent));
